<template>
  <div
    v-if="selectedStudents.length > 0"
    class="fixed bottom-4 right-4 z-50 bg-gray-800 text-white rounded-xl shadow-lg p-4 sm:p-5 animate-slide-up"
  >
    <div class="flex flex-col gap-3 sm:gap-4">
      <div class="flex items-center gap-2 sm:gap-3">
        <div class="p-1.5 sm:p-2 bg-blue-600 rounded-lg flex-shrink-0">
          <CheckSquare class="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 text-white" />
        </div>
        <div class="flex-1 min-w-0">
          <p class="text-sm sm:text-base font-semibold text-gray-100">
            {{ selectedStudents.length }} student{{
              selectedStudents.length > 1 ? "s" : ""
            }}
            selected
          </p>
          <p class="text-xs sm:text-sm text-gray-300">
            Choose an action to perform on selected students
          </p>
        </div>
      </div>
      <div
        class="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3"
      >
        <button
          @click="$emit('delete-selected')"
          :disabled="isLoading || isDeleting || isBookingSmartCard"
          class="inline-flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-red-600 text-white text-xs sm:text-sm font-semibold rounded-lg hover:bg-red-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed border border-red-600 min-h-[40px] sm:min-h-[36px]"
        >
          <div
            v-if="isDeleting"
            class="w-3 h-3 sm:w-4 sm:h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"
          ></div>
          <Trash2
            v-else
            class="w-3 h-3 sm:w-4 sm:h-4"
          />
          <span class="hidden xs:inline">{{
            isDeleting ? "Deleting..." : "Delete Selected"
          }}</span>
          <span class="xs:hidden">{{
            isDeleting ? "Deleting..." : "Delete"
          }}</span>
        </button>

        <!-- Added Book Smart Card button with loading state -->
        <button
          @click="$emit('book-smart-card')"
          :disabled="isLoading || isDeleting || isBookingSmartCard"
          class="inline-flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-purple-600 text-white text-xs sm:text-sm font-semibold rounded-lg hover:bg-purple-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed border border-purple-600 min-h-[40px] sm:min-h-[36px]"
        >
          <div
            v-if="isBookingSmartCard"
            class="w-3 h-3 sm:w-4 sm:h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"
          ></div>
          <CreditCard
            v-else
            class="w-3 h-3 sm:w-4 sm:h-4"
          />
          <span class="hidden xs:inline">{{
            isBookingSmartCard ? "Processing..." : "Book Smart Card"
          }}</span>
          <span class="xs:hidden">{{
            isBookingSmartCard ? "Processing..." : "Oder Cards"
          }}</span>
        </button>
        <button
          @click="showPromoteClassModal = true"
          :disabled="isLoading || isBookingSmartCard"
          class="inline-flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-green-600 text-white text-xs sm:text-sm font-semibold rounded-lg hover:bg-green-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed border border-green-600 min-h-[40px] sm:min-h-[36px]"
        >
          <TrendingUp class="w-3 h-3 sm:w-4 sm:h-4" />
          <span class="hidden xs:inline">Promote to Higher Class</span>
          <span class="xs:hidden">Change Class</span>
        </button>
        <button
          @click="$emit('clear-selection')"
          class="px-3 py-2 text-xs sm:text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-all duration-200 border border-gray-600 min-h-[40px] sm:min-h-[36px]"
        >
          Clear Selection
        </button>
      </div>
    </div>
  </div>

  <!-- Promote Class Modal -->
  <PromoteClassModal
    :show="showPromoteClassModal"
    :selectedStudentsCount="selectedStudents.length"
    :isPromoting="isLoading"
    @close="showPromoteClassModal = false"
    @confirm-promote="handleConfirmPromote"
  />
</template>

<script setup>
  import { ref } from "vue";
  import { CheckSquare, TrendingUp, Trash2, CreditCard } from "lucide-vue-next";
  import PromoteClassModal from "./PromoteClassModal.vue";

  const props = defineProps({
    selectedStudents: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    isDeleting: {
      type: Boolean,
      default: false,
    },
    isBookingSmartCard: {
      type: Boolean,
      default: false,
    },
  });

  const emit = defineEmits([
    "clear-selection",
    "promote-selected",
    "delete-selected",
    "promote-selected-with-class",
    "book-smart-card",
  ]);

  const showPromoteClassModal = ref(false);

  const handleConfirmPromote = ({ classId, session, year }) => {
    emit("promote-selected-with-class", {
      studentIds: props.selectedStudents,
      classId,
      session,
      year,
    });
    showPromoteClassModal.value = false;
  };
</script>

<style scoped>
  @keyframes slide-up {
    from {
      transform: translateY(100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
  .animate-slide-up {
    animation: slide-up 0.3s ease-out forwards;
  }
</style>
